import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PO_STATUSES } from "../../../constants/inventory";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Checkbox, IconButton, Snackbar, Alert,
  Skeleton,
} from "@mui/material";
import { MdDelete, MdVisibility, MdEdit } from "react-icons/md";

const PurchaseOrders = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllPurchaseOrders?page=${page + 1}&limit=${rowsPerPage}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (vendorFilter) url += `&vendor=${vendorFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setPurchaseOrders(data.purchaseOrders || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to fetch purchase orders", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await fetch("/api/getAllVendors?limit=100", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setVendors(data.vendors || []);
      }
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [page, rowsPerPage, statusFilter, vendorFilter]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const deletable = purchaseOrders
        .filter((po) => po.status === "draft" || po.status === "cancelled")
        .map((po) => po._id);
      setSelected(deletable);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const isDeletable = (status) => status === "draft" || status === "cancelled";

  const handleDelete = async () => {
    if (selected.length === 0) return;
    try {
      const response = await fetch("/api/deletePurchaseOrder", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount || selected.length} PO(s) deleted`, severity: "success" });
        setSelected([]);
        fetchPurchaseOrders();
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to delete", severity: "error" });
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  const getStatusBadge = (status) => {
    const config = PO_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const deletableCount = purchaseOrders.filter((po) => isDeletable(po.status)).length;

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <h2 className="text-xl font-semibold text-gray-800">Purchase Orders</h2>
        <button
          onClick={() => navigate("/PurchaseOrders/New")}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          + New PO
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(PO_STATUSES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        <select
          value={vendorFilter}
          onChange={(e) => { setVendorFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Vendors</option>
          {vendors.map((v) => (
            <option key={v._id} value={v._id}>{v.name}</option>
          ))}
        </select>

        {selected.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600"
          >
            <MdDelete /> Delete ({selected.length})
          </button>
        )}
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < deletableCount}
                    checked={deletableCount > 0 && selected.length === deletableCount}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>PO #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Items Count</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Expected Delivery</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : purchaseOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No purchase orders found</p>
                  </TableCell>
                </TableRow>
              ) : (
                purchaseOrders.map((po) => (
                  <TableRow
                    key={po._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/PurchaseOrders/View/${po._id}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      {isDeletable(po.status) && (
                        <Checkbox
                          checked={selected.includes(po._id)}
                          onChange={() => handleSelect(po._id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-primary">{po.poNumber}</span>
                    </TableCell>
                    <TableCell>{po.vendor?.name || "N/A"}</TableCell>
                    <TableCell>{po.items?.length || 0}</TableCell>
                    <TableCell>Nu. {po.totalAmount?.toLocaleString() || "0"}</TableCell>
                    <TableCell>
                      {po.expectedDeliveryDate
                        ? new Date(po.expectedDeliveryDate).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>{getStatusBadge(po.status)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/PurchaseOrders/View/${po._id}`)}
                        >
                          <MdVisibility className="text-primary" />
                        </IconButton>
                        {po.status === "draft" && (
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/PurchaseOrders/Edit/${po._id}`, { state: { purchaseOrder: po } })}
                          >
                            <MdEdit className="text-gray-500" />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PurchaseOrders;
